import React, { useState } from 'react';
import {
  FaMobileAlt,
  FaBuilding,
  FaBarcode,
  FaUser,
  FaTag,
  FaSimCard,
  FaStickyNote,
  FaSave,
  FaCalendarAlt
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { getApiError } from '../utils/apiError';
import { Modal } from './common';

function SmartphoneModal({ smartphone, onClose, onUpdate }) {
  const [notes, setNotes] = useState(smartphone?.notas || '');
  const [saving, setSaving] = useState(false);

  const getStatusBadge = (estado) => {
    const statusStyles = {
      disponible: 'bg-green-100 text-green-800',
      asignado: 'bg-sky-100 text-sky-800',
      reparacion: 'bg-amber-100 text-amber-800',
      'a desincorporar': 'bg-red-100 text-red-800',
      desincorporado: 'bg-red-100 text-red-800',
      baja: 'bg-red-100 text-red-800'
    };
    const style = statusStyles[estado?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase ${style}`}>
        {estado}
      </span>
    );
  };

  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      await axiosInstance.patch(`/api/smartphones/${smartphone.id}`, {
        notas: notes
      });
      if (onUpdate) {
        onUpdate({ ...smartphone, notas: notes });
      }
    } catch (error) {
      console.error('Error al guardar las notas:', error);
      toast.error(getApiError(error, 'No se pudieron guardar las notas'));
    } finally {
      setSaving(false);
    }
  };

  // Componente reutilizable para los items del grid
  const DetailItem = ({ icon: Icon, label, children, className = "" }) => (
    <div className={`flex items-start gap-3 p-4 bg-gray-50 rounded-xl ${className}`}>
      <Icon className="text-sura-blue text-lg mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong className="block mb-1 text-gray-500 text-xs font-semibold uppercase tracking-wider">
          {label}
        </strong>
        {children}
      </div>
    </div>
  );

  return (
    <Modal isOpen={!!smartphone} onClose={onClose} size="xl">
      <Modal.Header onClose={onClose}>
        Detalle del Smartphone
      </Modal.Header>
      <Modal.Body className="p-6">
        {/* ============================================
            HEADER
            ============================================ */}
        <div className="flex flex-col sm:flex-row gap-6 items-start pb-6 border-b-2 border-gray-100 mb-6">
          {/* Device Icon */}
          <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center">
            <FaMobileAlt className="text-5xl text-emerald-500" />
          </div>

          {/* Info Principal */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-400 font-mono">ID: {smartphone.id}</span>
            </div>
            <h2 className="m-0 text-gray-900 text-2xl font-bold mb-1">
              {smartphone.marca} {smartphone.modelo}
            </h2>
            <p className="m-0 text-gray-500 text-base">Smartphone</p>
          </div>

          {/* Estado y Activo Fijo */}
          <div className="flex flex-col items-end gap-3">
            {getStatusBadge(smartphone.estado)}
            <span className="font-mono text-lg font-bold text-sura-blue bg-blue-50 px-4 py-2 rounded-lg">
              {smartphone.activo_fijo || 'Sin AF'}
            </span>
          </div>
        </div>

        {/* ============================================
            DETAILS GRID
            ============================================ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailItem icon={FaTag} label="Marca y Modelo">
            <p className="m-0 text-gray-800 text-sm font-bold">{smartphone.marca || 'N/A'}</p>
            <p className="m-0 text-gray-600 text-sm">{smartphone.modelo || 'N/A'}</p>
          </DetailItem>

          <DetailItem icon={FaBarcode} label="Serial">
            <p className="m-0 font-mono text-sm font-medium text-gray-800 bg-gray-100 px-2 py-1 rounded inline-block">
              {smartphone.serial || 'N/A'}
            </p>
          </DetailItem>

          <DetailItem icon={FaSimCard} label="IMEI">
            <p className="m-0 font-mono text-sm text-gray-800">
              {smartphone.imei || 'N/A'}
            </p>
            {smartphone.imei2 && (
              <p className="m-0 font-mono text-xs text-gray-500 mt-1">
                IMEI 2: {smartphone.imei2}
              </p>
            )}
          </DetailItem>

          <DetailItem icon={FaUser} label="Asignado a">
            {smartphone.empleado ? (
              <p className="m-0 text-gray-800 text-sm font-medium">{smartphone.empleado}</p>
            ) : smartphone.asignacion_manual ? (
              <p className="m-0 text-amber-700 text-sm font-medium">{smartphone.asignacion_manual}</p>
            ) : (
              <p className="m-0 text-gray-400 text-sm italic">No asignado</p>
            )}
          </DetailItem>

          {smartphone.fecha_asignacion && (
            <DetailItem icon={FaCalendarAlt} label="Fecha de Asignación">
              <p className="m-0 text-gray-800 text-sm font-medium">
                {new Date(smartphone.fecha_asignacion).toLocaleDateString('es-ES')}
              </p>
            </DetailItem>
          )}

          {smartphone.linea && (
            <DetailItem icon={FaBuilding} label="Línea Telefónica">
              <p className="m-0 font-mono text-sm font-medium text-gray-800">{smartphone.linea}</p>
            </DetailItem>
          )}

          {/* Notas - Full Width */}
          <div className="sm:col-span-2 lg:col-span-3 flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
            <FaStickyNote className="text-sura-blue text-lg mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <strong className="block mb-2 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                Notas
              </strong>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-sura-blue focus:border-transparent"
                placeholder="Agregar notas sobre el smartphone..."
                rows="3"
              />
              <button
                onClick={handleSaveNotes}
                disabled={saving}
                className="mt-2 px-4 py-2 text-sm rounded-lg bg-sura-blue text-white font-medium flex items-center gap-2 hover:bg-sura-blue-dark transition-colors disabled:opacity-50"
              >
                <FaSave /> {saving ? 'Guardando...' : 'Guardar Notas'}
              </button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default SmartphoneModal;